import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { useApplyMacro, useMacros } from './queries';

interface MacroMenuProps {
  ticketId: string;
}

/**
 * Makros am Ticket -- F1-38, F1-39, K-20, K-65.
 *
 * Die Rueckmeldung nennt, was ausgefuehrt wurde und was nichts zu tun hatte.
 * Ein blosses "erledigt" waere hier zu wenig: Ein Makro, das Felder setzt und
 * eine Antwort schreibt, veraendert mehr, als der Bearbeiter auf einen Blick
 * nachvollziehen kann -- und was ein Kunde zu lesen bekommt, sollte niemand
 * raten muessen.
 *
 * Die Liste wird erst geholt, wenn das Menue offen ist. Ein Bestand, den die
 * meisten Ticketaufrufe nie brauchen, gehoert nicht in den ersten Ladevorgang.
 */
export function MacroMenu({ ticketId }: MacroMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const macros = useMacros(open);
  const apply = useApplyMacro(ticketId);

  const active = (macros.data ?? []).filter((macro) => macro.is_active);

  return (
    <div className="snippet-picker">
      <Button
        variant="secondary"
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        {t('macros.apply')}
      </Button>

      {open ? (
        <div className="snippet-picker__panel">
          {macros.isPending ? <p className="table__muted">{t('app.loading')}</p> : null}

          {macros.isSuccess && active.length === 0 ? (
            <p className="table__muted">{t('macros.none')}</p>
          ) : null}

          <ul className="snippet-picker__list">
            {active.map((macro) => (
              <li key={macro.id}>
                <Button
                  variant="ghost"
                  busy={apply.isPending && apply.variables === macro.id}
                  onClick={() => {
                    apply.mutate(macro.id, {
                      onSuccess: () => {
                        setOpen(false);
                      },
                    });
                  }}
                >
                  {macro.name}
                </Button>
                {macro.description ? (
                  <span className="table__muted"> {macro.description}</span>
                ) : null}
              </li>
            ))}
          </ul>

          {apply.isError ? <ErrorNotice error={apply.error} /> : null}
        </div>
      ) : null}

      {apply.data ? (
        <p className="field__hint" role="status">
          {t('macros.result', {
            applied: apply.data.applied.length,
            skipped: (apply.data.skipped ?? []).length,
          })}
        </p>
      ) : null}
    </div>
  );
}
